import { SessionCard } from './SessionCard';
import type { ScheduleSession } from '../../types/schedule.types';
import type { AgendaOption } from '../../types/agenda.types';
import { Clock } from 'lucide-react';

interface TimeSlotProps {
  time: string;
  endTime: string;
  sessions: ScheduleSession[];
  onSessionClick: (session: ScheduleSession) => void;
  onMaterialsClick?: (session: ScheduleSession) => void;
  viewType?: 'timeline' | 'list';
  agendaOptions?: AgendaOption;
  primaryColor?: string;
  eventDate?: string;
  eventTimeZone?: string;
  eventDetails?: any; // For calendar icons
  // Optional expanded session card component for list view
  ExpandedSessionCard?: React.ComponentType<{
    session: ScheduleSession;
    onClick: () => void;
    onMaterialsClick?: () => void;
    agendaOptions?: AgendaOption;
    primaryColor?: string;
    eventDate?: string;
    eventTimeZone?: string;
  }>;
}

export const TimeSlot = ({
  time,
  endTime,
  sessions,
  onSessionClick,
  onMaterialsClick,
  viewType = 'timeline',
  agendaOptions,
  primaryColor = '#2563eb',
  eventDate,
  eventTimeZone,
  eventDetails,
  ExpandedSessionCard
}: TimeSlotProps) => {
  // Group breakout sessions
  const regularSessions = sessions.filter(session => !session.isBreakout);
  const breakoutGroups = sessions.filter(session => session.isBreakout).reduce((acc, session) => {
    const group = session.breakoutGroup || 'Breakout Sessions';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(session);
    return acc;
  }, {} as Record<string, ScheduleSession[]>);

  // Render session based on view type
  const renderSession = (session: ScheduleSession, compact = false) => {
    if (viewType === 'list' && ExpandedSessionCard) {
      return (
        <ExpandedSessionCard
          key={session.id}
          session={session}
          onClick={() => onSessionClick(session)}
          onMaterialsClick={onMaterialsClick ? () => onMaterialsClick(session) : undefined}
          agendaOptions={agendaOptions}
          primaryColor={primaryColor}
          eventDate={eventDate}
          eventTimeZone={eventTimeZone}
        />
      );
    }
    return (
      <SessionCard
        key={session.id}
        session={session}
        onClick={() => onSessionClick(session)}
        compact={compact}
        agendaOptions={agendaOptions}
        primaryColor={primaryColor}
        eventDate={eventDate}
        eventTimeZone={eventTimeZone}
        eventDetails={eventDetails}
      />
    );
  };

  return <div className="relative mb-8">
      <div className="flex items-center gap-2 text-gray-700 mb-3 px-2">
        <Clock size={16} style={{ color: primaryColor }} />
        <div className="font-medium" style={{ color: primaryColor }}>{time}</div>
      </div>
      <div className="space-y-4">
        {/* Regular sessions */}
        {regularSessions.length > 0 && <div className="space-y-3">
            {regularSessions.map(session => renderSession(session))}
          </div>}
        {/* Breakout sessions */}
        {Object.entries(breakoutGroups).map(([groupName, sessions]) => <div key={groupName} className="space-y-2">
            <h3 className="text-sm font-medium text-gray-600 pl-2 border-l-2" style={{ borderLeftColor: primaryColor }}>
              {groupName}
            </h3>
            <div className={`grid grid-cols-1 ${viewType === 'list' ? '' : 'md:grid-cols-2 lg:grid-cols-3'} gap-3`}>
              {sessions.map(session => renderSession(session, viewType !== 'list'))}
            </div>
          </div>)}
      </div>
    </div>;
};